import { useCallback, useEffect, useRef } from "react";
import { ColumnMovedEvent, NewColumnsLoadedEvent } from "ag-grid-community";

import { useColumnMovement } from "../Header/hooks/useColumnMovement";
import { useReportContext } from "../../report-context";

import { ReportColumn } from "@/types/evaluate";
import { usePrevious } from "@/hooks/usePrevious";

export const useColumnEvents = (reportId: string) => {
  const reportContext = useReportContext();
  const { sortedColumns } = reportContext;
  const { handleMoveColumn } = useColumnMovement({
    reportId: Number(reportId),
  });

  const previousColumns = usePrevious(sortedColumns);
  const previousColumnCount = previousColumns?.length || 0;
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    },
    [],
  );

  const handleColumnMoved = useCallback(
    (event: ColumnMovedEvent) => {
      if (!event.finished) {
        return;
      }

      if (!event.column || event.toIndex === undefined) {
        return;
      }

      const columnId = event.column.getColId()?.replace("col_", "");
      if (!columnId) {
        return;
      }

      const reportColumn = sortedColumns.find((col: ReportColumn) => String(col.id) === columnId);

      if (reportColumn) {
        handleMoveColumn(reportColumn, event.toIndex - 1);
      }
    },
    [handleMoveColumn, sortedColumns],
  );

  const handleNewColumnsLoaded = useCallback(
    (params: NewColumnsLoadedEvent) => {
      const currentColumnCount = sortedColumns.length;
      const isNewColumnAdded = currentColumnCount > previousColumnCount;

      if (!isNewColumnAdded || !previousColumns) {
        return;
      }

      const newlyAddedColumn = sortedColumns.find(
        (currentCol: ReportColumn) =>
          !previousColumns.some((prevCol: ReportColumn) => prevCol.id === currentCol.id),
      );

      if (newlyAddedColumn) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }

        timerRef.current = setTimeout(() => {
          const colId = `col_${newlyAddedColumn.id}`;
          try {
            params.api.ensureColumnVisible(colId);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.warn(`Failed to ensure column visibility for ${colId}:`, error);
          }
          timerRef.current = null;
        }, 300);
      }
    },
    [sortedColumns, previousColumns, previousColumnCount],
  );

  return {
    handleColumnMoved,
    handleNewColumnsLoaded,
  };
};

export default useColumnEvents;
